import type { SseEvent } from "types/message";
import { getToken } from "utils/auth";

interface StreamChatParam {
  conversationId: number;
  content: string;
  files?: File[];
}

const buildStreamBody = (data: StreamChatParam): BodyInit => {
  const files = data.files?.filter(Boolean) ?? [];

  if (files.length === 0) {
    return JSON.stringify({
      conversationId: data.conversationId,
      content: data.content,
      stream: true,
    });
  }

  const formData = new FormData();
  formData.append("conversationId", String(data.conversationId));
  formData.append("content", data.content);
  formData.append("stream", "true");
  files.forEach((file) => {
    formData.append("files", file);
  });
  return formData;
};

export const chatApi = {
  stream: async (
    data: StreamChatParam,
    onEvent: (event: SseEvent) => void,
  ): Promise<void> => {
    const baseUrl = import.meta.env.VITE_API_URL.replace(/\/$/, "");
    const hasFiles = (data.files?.length ?? 0) > 0;
    const body = buildStreamBody(data);

    const headers: Record<string, string> = {
      token: getToken(),
    };
    // Let the browser set multipart boundary when sending FormData.
    if (!hasFiles) {
      headers["Content-Type"] = "application/json";
    }

    const response = await fetch(`${baseUrl}/v1/admin/chat/stream`, {
      method: "POST",
      headers,
      body,
    });

    if (!response.ok) {
      let message = "Không thể gửi tin nhắn";
      try {
        const errorBody = await response.json();
        message = errorBody?.message || message;
      } catch {
        // ignore parse error
      }
      throw new Error(message);
    }

    if (!response.body) {
      throw new Error("Stream không khả dụng");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split("\n\n");
      buffer = parts.pop() ?? "";

      for (const part of parts) {
        const line = part
          .split("\n")
          .find((item) => item.startsWith("data:"));
        if (!line) {
          continue;
        }

        const raw = line.replace(/^data:\s*/, "").trim();
        if (!raw) {
          continue;
        }

        try {
          const event = JSON.parse(raw) as SseEvent;
          onEvent(event);
          if (event.type === "error") {
            throw new Error(String(event.data || "Lỗi xử lý chat"));
          }
        } catch (error) {
          if (error instanceof SyntaxError) {
            continue;
          }
          throw error;
        }
      }
    }
  },
};
